import { FC, useEffect } from 'react';
import { PushTokenEvent, PushErrorEvent } from '@/services/push-notification-service';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';

/**
 * This component listens for push notification events
 * It registers token when a new one is received and handles errors
 */
export const PushNotificationListener: FC = () => {
  const { toast } = useToast();

  useEffect(() => {
    // Only proceed if running on a native platform (iOS or Android)
    if (!Capacitor.isNativePlatform()) return;

    // Listen for push registration token events
    const handlePushToken = async (event: Event) => {
      const tokenEvent = event as PushTokenEvent;
      
      if (tokenEvent.detail?.token) {
        const { token, platform } = tokenEvent.detail;
        
        // Token is already sent to server in the PushNotificationService,
        // but we can use this event for any additional handling
        console.log(`Push token registered: ${token.substring(0, 8)}...`);
      }
    };
    
    // Listen for push notification errors
    const handlePushError = (event: Event) => {
      const errorEvent = event as PushErrorEvent;
      
      if (errorEvent.detail?.error) {
        console.error('Push notification error:', errorEvent.detail.error);
        
        // Notify user of push notification issues
        toast({
          title: 'Push Notification Issue',
          description: 'Unable to register for push notifications. You may miss important alerts.',
          variant: 'warning',
          duration: 5000,
        });
      }
    };
    
    // Register event listeners
    window.addEventListener('push_token', handlePushToken);
    window.addEventListener('push_error', handlePushError);
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('push_token', handlePushToken);
      window.removeEventListener('push_error', handlePushError);
    };
  }, [toast]);
  
  // This is a behavior component, so it doesn't render anything
  return null;
};

export default PushNotificationListener;